import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MdSearch,
  MdHistory,
  MdPersonSearch,
  MdPersonOff,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import CommonTable from "../../common/CommonTable";
import { API_URL } from "../../utils/constants";
import { cssProperties } from "../../utils/commonCssProperties";
import masterActions from "../../redux/master/actions";
import commonActions from "../../redux/common/actions";

/**
 * Download log, user wise - the full audit trail for one candidate on one course.
 *
 * Shared by the Hall Ticket and Score Card screens; they differ only in the API
 * path and what the document is called, so both are props.
 *
 * Searched by course + Application Number (exact match; stray spaces are
 * trimmed). The backend answers "not found" separately from "never downloaded",
 * so the candidate panel shows even when there are no downloads.
 */

const BRAND = cssProperties?.color?.primary;
const BUTTON_BG = cssProperties?.buttonbgcolor?.primary;
const BUTTON_FG = cssProperties?.buttonfontcolor?.black;
const MUTED = cssProperties?.color?.textsecondary;
const BORDER = cssProperties?.bordercolor?.secondary;

const panelSx = {
  p: { xs: 2, md: 3 },
  mb: 2.5,
  borderRadius: "16px",
  border: `1px solid ${BORDER}`,
  boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
  backgroundColor: cssProperties?.backgroundcolor?.secondary,
};

const labelSx = { mb: 0.75, fontSize: 13, fontWeight: 500, color: MUTED };

const pillButtonSx = {
  borderRadius: "40px",
  px: 3,
  py: 1.1,
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: "0.32px",
  boxShadow: "none",
  whiteSpace: "nowrap",
};

const primaryButtonSx = {
  ...pillButtonSx,
  backgroundColor: BUTTON_BG,
  color: BUTTON_FG,
  "&:hover": { backgroundColor: BUTTON_BG, boxShadow: "none", filter: "brightness(0.96)" },
};

const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

// Name and application number are already in the candidate panel above, so
// the history table only carries what differs per download.
const columnData = [
  {
    name: "#",
    dataIndex: "serial",
    render: (_v, row) => row.serial,
  },
  {
    name: "Downloaded At",
    dataIndex: "downloadtimestamp",
    // Already IST text from the API - re-parsing it as a Date would shift it
    // by the browser's offset.
    render: (value) => value || "-",
  },
  { name: "IP Address", dataIndex: "ipaddress", render: (v) => v || "-" },
  {
    name: "Device / Browser",
    dataIndex: "browser",
    // The API parses the user agent into "Chrome 154" / "Windows 10/11". The
    // raw string (which also names Mozilla, AppleWebKit and Safari as
    // compatibility tokens) is kept on hover as the evidence.
    render: (value, row) => {
      const label = [value, row.os].filter(Boolean).join(" · ");
      if (!label && !row.useragent) return "-";
      return (
        <Tooltip
          arrow
          title={
            row.useragent ? (
              <Box component="span" sx={{ fontSize: 12, wordBreak: "break-word" }}>
                {row.useragent}
              </Box>
            ) : (
              ""
            )
          }
        >
          <Box component="span" sx={{ cursor: row.useragent ? "help" : "default" }}>
            {label || "Unknown"}
          </Box>
        </Tooltip>
      );
    },
  },
  { name: "Platform", dataIndex: "platform", render: (v) => v || "-" },
];

const EmptyState = ({ icon: Icon, title, text }) => (
  <Box sx={{ textAlign: "center", py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: "auto",
        mb: 2,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: cssProperties?.hoverbgcolor?.sidebar,
        color: BRAND,
      }}
    >
      <Icon size={30} />
    </Box>
    <Typography sx={{ fontSize: 16, fontWeight: 600, color: cssProperties?.fontcolor?.primary }}>
      {title}
    </Typography>
    {text && (
      <Typography sx={{ mt: 0.5, fontSize: 14, color: MUTED, maxWidth: 420, mx: "auto" }}>
        {text}
      </Typography>
    )}
  </Box>
);

const Detail = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 12, color: MUTED, mb: 0.25 }}>{label}</Typography>
    <Typography sx={{ fontSize: 15, fontWeight: 600, wordBreak: "break-word" }}>
      {value || "-"}
    </Typography>
  </Box>
);

export default function DownloadUserLog({ apiPath, documentLabel }) {
  const dispatch = useDispatch();
  const { courseList } = useSelector((state) => state.masterReducer);

  const [courseId, setCourseId] = useState(0);
  const [applicationNo, setApplicationNo] = useState("");
  const [rows, setRows] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [notFound, setNotFound] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch({ type: masterActions.GET_COURSE_LIST });
  }, [dispatch]);

  // The shared toast never closes itself - the screen that opens it must hide
  // it, as the other screens do after 2s. The timer is kept so a newer alert
  // is not cut short by an older one's timeout.
  const alertTimer = useRef(null);

  const hideAlert = () => {
    clearTimeout(alertTimer.current);
    dispatch({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  };

  const showError = (message) => {
    clearTimeout(alertTimer.current);
    dispatch({
      type: commonActions.SET_ALERT,
      payload: { show: true, status: "failed", message },
    });
    alertTimer.current = setTimeout(hideAlert, 2000);
  };

  // Leaving the page must not strand an open alert on the next screen.
  useEffect(
    () => () => {
      clearTimeout(alertTimer.current);
      dispatch({
        type: commonActions.SET_ALERT,
        payload: { status: null, show: false, message: null },
      });
    },
    [dispatch]
  );

  const resetResults = () => {
    setCandidate(null);
    setRows(null);
    setNotFound(null);
  };

  const handleSearch = async () => {
    const appNo = applicationNo.trim();

    if (!courseId || !appNo) {
      showError("Select a course and enter an Application Number");
      return;
    }

    // A previous search's error must not sit over this search's result.
    hideAlert();
    resetResults();
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/${apiPath}/user-log?applicationno=${encodeURIComponent(
          appNo
        )}&courseid=${courseId}`
      );
      const result = response?.data?.result || [];
      setRows(result.map((row, i) => ({ ...row, serial: i + 1 })));
      setCandidate(response?.data?.candidate || null);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        `Failed to load ${documentLabel.toLowerCase()} logs`;
      // "No such application number" stays on the page (the toast fades);
      // anything else is a real failure and only gets the toast.
      if (error?.response?.status === 404) setNotFound(message);
      else showError(message);
    } finally {
      setLoading(false);
    }
  };

  const downloadCount = rows ? rows.length : 0;

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      {/* Search */}
      <Paper sx={panelSx}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 0.5 }}>Search Candidate</Typography>
        <Typography sx={{ fontSize: 14, color: MUTED, mb: 2.5 }}>
          Select the course and enter the candidate's Application Number.
        </Typography>

        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={5}>
            <Typography sx={labelSx}>Course</Typography>
            <Select
              fullWidth
              size="small"
              value={courseId}
              displayEmpty
              sx={{ borderRadius: "10px", backgroundColor: "#fff" }}
              onChange={(e) => {
                // Results belong to the course they were searched under;
                // clearing them keeps the download buttons from pairing this
                // candidate with a different course.
                setCourseId(e.target.value);
                resetResults();
              }}
            >
              <MenuItem value={0} disabled>
                <Typography component="span" sx={{ color: MUTED }}>
                  Select a course
                </Typography>
              </MenuItem>
              {courseList?.all?.map((course, i) => (
                <MenuItem key={i} value={course.courseid} sx={{ whiteSpace: "nowrap" }}>
                  {course.coursename}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography sx={labelSx}>Application No.</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Application Number"
              value={applicationNo}
              onChange={(e) => setApplicationNo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              inputProps={{ inputMode: "numeric", autoComplete: "off" }}
              InputProps={{
                sx: { borderRadius: "10px", backgroundColor: "#fff" },
                startAdornment: (
                  <InputAdornment position="start">
                    <MdSearch size={20} color={MUTED} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleSearch}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={16} sx={{ color: BUTTON_FG }} /> : <MdSearch />
              }
              sx={primaryButtonSx}
            >
              {loading ? "Searching..." : "Search"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Before any search */}
      {!loading && !candidate && !notFound && (
        <Paper sx={panelSx}>
          <EmptyState
            icon={MdPersonSearch}
            title="Find a candidate"
            text={`Search by Application Number to see the candidate's ${documentLabel.toLowerCase()} details and download history.`}
          />
        </Paper>
      )}

      {/* Searched, nothing matched */}
      {!loading && notFound && (
        <Paper sx={panelSx}>
          <EmptyState icon={MdPersonOff} title="No candidate found" text={notFound} />
        </Paper>
      )}

      {/* Candidate */}
      {!loading && candidate && (
        <>
          <Paper sx={panelSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Avatar
                sx={{
                  width: 52,
                  height: 52,
                  fontSize: 18,
                  fontWeight: 600,
                  backgroundColor: cssProperties?.hoverbgcolor?.sidebar,
                  color: BRAND,
                }}
              >
                {initials(candidate.candidatename)}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>
                  {candidate.candidatename || "-"}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <Detail label="Application No." value={candidate.applicationnumber} />
              </Grid>
              <Grid item xs={12} sm={8} md={6}>
                {/* Same value the admit card prints as "Name of the Course" -
                    the candidate's exam, not the course picked in the dropdown. */}
                <Detail
                  label="Course"
                  value={candidate.examcoursename || candidate.coursename}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Detail label="Total Downloads" value={String(downloadCount)} />
              </Grid>
            </Grid>
          </Paper>

          {/* History */}
          <Paper sx={panelSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: downloadCount ? 2 : 0 }}>
              <MdHistory size={20} color={BRAND} />
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Download History</Typography>
              <Chip
                size="small"
                label={downloadCount}
                sx={{ ml: 0.5, fontWeight: 600, backgroundColor: cssProperties?.surface?.primary }}
              />
            </Box>

            {downloadCount > 0 ? (
              <CommonTable tableData={rows} columnData={columnData} />
            ) : (
              <EmptyState
                icon={MdHistory}
                title="No downloads yet"
                text={`The candidate has not downloaded their ${documentLabel.toLowerCase()} so far.`}
              />
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}
